import { useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Moon, Rocket } from 'lucide-react';
import { CARD_CLASS, OMS_COLORS, tint } from '../../dashboard/omsDashTheme';
import { ADMIN_EYEBROW_CLASS, USAGE_COLOR } from '../adminTheme';
import { formatNumber } from '../../dashboard/utils/formatNumber';
import type { EngineerMomentum } from '../utils/adminMetrics';
import { MiniSpark, UsageChip } from './adminBits';

interface Props {
  rows: EngineerMomentum[];
  phrase: string;
  loading: boolean;
  onOpen: (engineer: EngineerMomentum) => void;
}

const PREVIEW_COUNT = 10;

function Rail({
  title,
  icon: Icon,
  tone,
  rows,
  onOpen,
}: {
  title: string;
  icon: typeof Rocket;
  tone: { ink: string; fill: string };
  rows: EngineerMomentum[];
  onOpen: (engineer: EngineerMomentum) => void;
}) {
  return (
    <div className="min-w-0 flex-1 rounded-xl border border-[rgba(11,59,46,0.16)] p-2.5" style={{ backgroundColor: tint(tone.fill, 0.05) }}>
      <p className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: tone.ink }}>
        <Icon className="h-3 w-3" /> {title}
      </p>
      {rows.length === 0 ? (
        <p className="mt-1.5 text-[10.5px] font-medium" style={{ color: OMS_COLORS.caption }}>
          No one in this window.
        </p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {rows.slice(0, 3).map((row) => (
            <li key={row.engineerId}>
              <button
                type="button"
                onClick={() => onOpen(row)}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-1.5 py-1 text-left transition hover:bg-[rgba(11,59,46,0.05)]"
              >
                <span className="truncate text-[11.5px] font-bold" style={{ color: OMS_COLORS.tealDark }}>
                  {row.name}
                </span>
                <span className="shrink-0 text-[10.5px] font-extrabold tabular-nums" style={{ color: tone.ink }}>
                  {row.changePct === null ? '—' : `${row.changePct > 0 ? '+' : ''}${row.changePct}%`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * ★ Engineer usage momentum — every engineer classified Growing / Steady /
 * Declining / Dormant with the % change behind the label, a weekly
 * sparkline and last-active. Recognition rail first: celebrate the fastest
 * growing before flagging the dormant.
 */
export default function UsageMomentumRoster({ rows, phrase, loading, onOpen }: Props) {
  const [expanded, setExpanded] = useState(false);
  const growing = rows.filter((r) => r.usage === 'growing');
  const declining = rows.filter((r) => r.usage === 'declining').sort((a, b) => (a.changePct ?? 0) - (b.changePct ?? 0));
  const dormant = rows.filter((r) => r.usage === 'dormant');
  const visible = expanded ? rows : rows.slice(0, PREVIEW_COUNT);

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      aria-label="Engineer usage momentum"
      className={`${CARD_CLASS} p-4`}
    >
      <div aria-hidden className="pointer-events-none absolute inset-[7px] rounded-[11px] border border-dashed border-[rgba(11,59,46,0.14)]" />
      <div className="relative">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className={ADMIN_EYEBROW_CLASS}>Engineer usage momentum · {formatNumber(rows.length)} engineers</p>
          <p className="text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
            Change compares the window's second half with its first · dormant = no app action for 14+ days
          </p>
        </div>

        {loading && rows.length === 0 ? (
          <div className="mt-3 space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-11 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm font-medium" style={{ color: OMS_COLORS.caption }}>
            No engineer activity in this scope — widen the range or clear filters.
          </p>
        ) : (
          <>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Rail title="Fastest growing" icon={Rocket} tone={USAGE_COLOR.growing} rows={growing} onOpen={onOpen} />
              <Rail title="Declining fastest" icon={Flame} tone={USAGE_COLOR.declining} rows={declining} onOpen={onOpen} />
              <Rail title="Went dormant" icon={Moon} tone={USAGE_COLOR.dormant} rows={dormant} onOpen={onOpen} />
            </div>

            <ul className="mt-3 space-y-1 border-t border-[rgba(11,59,46,0.10)] pt-2.5">
              {visible.map((row, index) => (
                <motion.li
                  key={row.engineerId}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(index * 0.025, 0.25), duration: 0.25 }}
                >
                  <button
                    type="button"
                    data-perf-row
                    onClick={() => onOpen(row)}
                    aria-label={`Open usage detail for ${row.name}`}
                    className="grid w-full grid-cols-[auto_minmax(0,1.6fr)_auto_auto_minmax(0,1fr)] items-center gap-3 rounded-xl border border-transparent px-2.5 py-2 text-left transition hover:border-[rgba(11,59,46,0.16)] hover:bg-[rgba(11,59,46,0.04)] focus-visible:ring-2 focus-visible:ring-[rgba(20,96,107,0.45)]"
                  >
                    <span className="w-5 text-center text-[11px] font-extrabold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                      {rows.indexOf(row) + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[13px] font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                          {row.name}
                        </span>
                        <UsageChip usage={row.usage} />
                      </span>
                      <span className="mt-0.5 block truncate text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                        {row.roleName || 'Site role'} ·{' '}
                        {row.daysSinceActive === null
                          ? 'no activity in window'
                          : row.daysSinceActive === 0
                            ? 'active today'
                            : `last active ${row.daysSinceActive}d ago`}
                      </span>
                    </span>
                    <span
                      className="w-14 text-right text-sm font-extrabold tabular-nums"
                      style={{ color: USAGE_COLOR[row.usage].ink }}
                      title={`Second-half vs first-half app actions ${phrase}`}
                    >
                      {row.changePct === null ? '—' : `${row.changePct > 0 ? '+' : ''}${row.changePct}%`}
                    </span>
                    <MiniSpark values={row.weekly} label={`${row.name} weekly app actions`} stroke={USAGE_COLOR[row.usage].fill} />
                    <span className="hidden text-right text-[10.5px] font-bold tabular-nums sm:block" style={{ color: OMS_COLORS.caption }}>
                      <span style={{ color: '#1F5A47' }}>{formatNumber(row.actions)} actions</span>
                      {' · '}
                      {formatNumber(row.completed)} completed · {formatNumber(row.skipped)} skipped
                    </span>
                  </button>
                </motion.li>
              ))}
            </ul>
            {rows.length > PREVIEW_COUNT && !expanded && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="mt-2 w-full rounded-xl border border-[rgba(11,59,46,0.2)] py-2 text-[11px] font-extrabold uppercase tracking-[0.1em] transition hover:bg-[rgba(11,59,46,0.05)]"
                style={{ color: '#14606B', backgroundColor: '#fcfaf3' }}
              >
                View all {formatNumber(rows.length)} engineers →
              </button>
            )}
          </>
        )}
      </div>
    </motion.section>
  );
}
